/**
 * LE SUIVI D'UNE CLASSE — lot P3 (AB#975, épopée AB#961).
 * `/classes/:id/parcours`.
 *
 * Chaque parcours déposé à la classe, et une ligne par élève : « 3 sur 7 »,
 * son état, en retard ou non (`GET …/cohortes/:id/parcours`, contrat
 * `CONTRAT-PARCOURS.md`). Maquette : `Tuteur-Suivi.dc.html`, corrigée par le
 * fondateur (19 oct. 2026, AB#961) : PAS DE RAPPEL — « Envoyer un rappel » est
 * retiré — et UN SEUL NOMBRE par élève, « 3 sur 7 », sans colonne par bloc.
 *
 * Les comptes de la classe (terminé, en cours, en pause, pas commencé, en
 * retard) sont des DÉNOMBREMENTS d'élèves posés côte à côte, jamais l'un
 * rapporté à l'autre (`DECISIONS-ARCHITECTE.md`, point 17).
 *
 * Tant que la route n'est pas servie (P1), elle répond 404 : la page le lit
 * comme « aucun parcours déposé », pas comme une panne.
 */
import { denombrer } from '@silex/shared';
import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import {
  ApiError,
  api,
  type EleveDansParcoursDeClasse,
  type ParcoursDeClasse,
  type SuiviParcoursClasseResponse,
} from '../api/client';
import { Badge } from '../components/Badge';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { manuelVivantActif } from '../lib/manuel-vivant';
import {
  LIBELLES_ETAT_PARCOURS,
  adressesManuelEleve,
  adressesParcours,
  faitesSurTotal,
  jourCivil,
} from '../lib/manuel-tuteur-format';
import type { BadgeTone } from '../lib/suivi-format';
import { formatDate } from '../lib/tuteur-format';
import { useAsync } from '../lib/useAsync';

/** Où en est un élève devant un parcours, dit en un mot. « Pas commencé » : rien de fait. */
export type EtatDEleve = 'termine' | 'en_cours' | 'en_pause' | 'pas_commence' | 'archive';

export function etatDEleve(e: Pick<EleveDansParcoursDeClasse, 'etat' | 'faites'>): EtatDEleve {
  if (e.etat === 'en_cours' && e.faites === 0) return 'pas_commence';
  return e.etat;
}

const LIBELLES_ETAT_ELEVE: Readonly<Record<EtatDEleve, string>> = {
  ...LIBELLES_ETAT_PARCOURS,
  pas_commence: 'Pas commencé',
};

const TON_ETAT_ELEVE: Readonly<Record<EtatDEleve, BadgeTone>> = {
  termine: 'acquis',
  en_cours: 'en-cours',
  en_pause: 'a-revoir',
  pas_commence: 'a-revoir',
  archive: 'a-revoir',
};

/** Les dénombrements de la classe, dans l'ordre de la maquette. Un compte nul ne s'affiche pas. */
export function comptesDeLaClasse(
  p: ParcoursDeClasse,
): { readonly cle: string; readonly libelle: string; readonly n: number }[] {
  const compter = (etat: EtatDEleve): number =>
    p.eleves.filter((e) => etatDEleve(e) === etat).length;
  return [
    { cle: 'termine', libelle: 'Terminé', n: compter('termine') },
    { cle: 'en_cours', libelle: 'En cours', n: compter('en_cours') },
    { cle: 'en_pause', libelle: 'En pause', n: compter('en_pause') },
    { cle: 'pas_commence', libelle: 'Pas commencé', n: compter('pas_commence') },
    { cle: 'en_retard', libelle: 'En retard', n: p.eleves.filter((e) => e.enRetard).length },
  ].filter((c) => c.n > 0);
}

/** Le suivi, ou « aucun parcours » quand la route n'est pas encore servie (404). */
export async function lireSuiviDeClasse(classeId: string): Promise<SuiviParcoursClasseResponse> {
  try {
    return await api.manuelSuiviParcoursClasse(classeId);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { parcours: [] };
    throw err;
  }
}

export function SuiviParcoursClassePage() {
  const { id } = useParams<{ id: string }>();
  const actif = manuelVivantActif();
  const classes = useAsync(useMemo(() => (actif ? () => api.listerClasses() : null), [actif]));
  const suivi = useAsync(
    useMemo(() => (actif && id ? () => lireSuiviDeClasse(id) : null), [actif, id]),
  );

  if (!actif) return <Navigate to="/" replace />;
  if (!id) return <Navigate to="/classes" replace />;

  const classe = classes.status === 'ready' ? classes.data.classes.find((c) => c.id === id) : null;

  return (
    <div className="content__inner manuel-tuteur">
      <p className="note-line fil-retour">
        <Link to="/classes">← Mes classes</Link>
      </p>
      {classes.status === 'loading' || suivi.status === 'loading' ? (
        <Chargement quoi="le suivi de la classe" />
      ) : null}
      {classes.status === 'error' ? <ErreurRessource message={classes.message} /> : null}
      {suivi.status === 'error' ? <ErreurRessource message={suivi.message} /> : null}
      {classes.status === 'ready' && classe === undefined ? (
        <ErreurRessource message="Cette classe n’est pas l’une des vôtres." />
      ) : null}
      {classe && suivi.status === 'ready' ? (
        <SuiviDeClasse
          classeId={classe.id}
          nom={classe.nom}
          nbEleves={classe.nbMembres}
          suivi={suivi.data}
        />
      ) : null}
    </div>
  );
}

/** Le suivi, une fois lu : son rendu s'éprouve sans serveur. */
export function SuiviDeClasse({
  classeId,
  nom,
  nbEleves,
  suivi,
}: {
  readonly classeId: string;
  readonly nom: string;
  readonly nbEleves: number;
  readonly suivi: SuiviParcoursClasseResponse;
}) {
  const [choisi, setChoisi] = useState<string | null>(suivi.parcours[0]?.id ?? null);
  const parcours = suivi.parcours.find((p) => p.id === choisi) ?? suivi.parcours[0] ?? null;

  return (
    <>
      <div className="dash-topbar">
        <div>
          <p className="eyebrow">{denombrer(nbEleves, 'élève')}</p>
          <h1 data-testid="suivi-classe-titre">{nom}</h1>
        </div>
        <Link
          className="btn btn--primary btn--sm"
          to={adressesParcours.composer({ classe: classeId })}
          data-testid="suivi-classe-nouveau"
        >
          Nouveau parcours
        </Link>
      </div>

      {parcours === null ? (
        <p className="note-line" data-testid="suivi-classe-vide">
          Aucun parcours déposé à cette classe.
        </p>
      ) : (
        <>
          {suivi.parcours.length > 1 ? (
            <div className="segmented" role="group" aria-label="Parcours">
              {suivi.parcours.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={p.id === parcours.id ? 'segmented__btn is-active' : 'segmented__btn'}
                  aria-pressed={p.id === parcours.id}
                  onClick={() => setChoisi(p.id)}
                  data-testid="suivi-classe-parcours"
                >
                  {p.nom}
                </button>
              ))}
            </div>
          ) : null}
          <ParcoursDeLaClasse parcours={parcours} />
        </>
      )}
    </>
  );
}

function ParcoursDeLaClasse({ parcours: p }: { readonly parcours: ParcoursDeClasse }) {
  const comptes = comptesDeLaClasse(p);
  return (
    <section className="card" data-testid="suivi-classe-carte">
      <h3>{p.nom}</h3>
      <p className="card-sub">
        {[
          `Assigné le ${formatDate(p.creeLe)}`,
          p.pourLe === null ? 'sans date butoir' : `pour le ${jourCivil(p.pourLe)}`,
          denombrer(p.total, 'étape'),
        ].join(' · ')}
      </p>
      {comptes.length > 0 ? (
        <p className="row wrap" data-testid="suivi-classe-comptes">
          {comptes.map((c) => (
            <span key={c.cle} className="cell-sub">
              <strong>{c.n}</strong> {c.libelle}
            </span>
          ))}
        </p>
      ) : null}

      <table className="data-table">
        <thead>
          <tr>
            <th>Élève</th>
            <th>Avancement</th>
            <th>État</th>
            <th>
              <span className="sr-only">Gestes</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {p.eleves.map((e) => {
            const etat = etatDEleve(e);
            return (
              <tr key={e.parcoursId} data-testid="suivi-classe-eleve">
                <td>{e.prenom}</td>
                <td>
                  <strong>{faitesSurTotal(e.faites, e.total)}</strong>
                </td>
                <td>
                  <span className="row wrap">
                    <Badge tone={TON_ETAT_ELEVE[etat]} label={LIBELLES_ETAT_ELEVE[etat]} />
                    {e.enRetard ? <Badge tone="a-revoir" label="En retard" /> : null}
                  </span>
                </td>
                <td>
                  <Link
                    className="btn btn--ghost btn--sm"
                    to={adressesManuelEleve.fiche(e.eleveId, 'sequences')}
                  >
                    Voir
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
